/**
 * HistoryDialog — version snapshots for a canvas.
 * "Save version" snapshots the board's current persisted state; each row can be
 * restored (which first auto-backs-up the current board, so restore is safe).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, History, RotateCcw, Camera, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { T, ConfirmDialog } from '../../../components/em/ui';
import { fmtDate } from '../wsConstants';
import canvasApi from './canvasApi';
import askPrompt from '../../../components/em/askPrompt';

export default function HistoryDialog({ canvasId, onBeforeSnapshot, onRestored, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    canvasApi.listVersions(canvasId)
      .then(setVersions)
      .catch(() => toast.error('Could not load history'))
      .finally(() => setLoading(false));
  }, [canvasId]);

  useEffect(() => { load(); }, [load]);

  const snapshot = async () => {
    setBusy(true);
    try {
      // Persist the live board first so the snapshot captures the latest edits.
      await onBeforeSnapshot?.();
      const label = await askPrompt('Label this version (optional):', '');
      if (label === null) { setBusy(false); return; }
      await canvasApi.createVersion(canvasId, label.trim() || null);
      toast.success('Version saved');
      load();
    } catch { toast.error('Could not save version'); }
    finally { setBusy(false); }
  };

  const [confirming, setConfirming] = useState(null);

  const restore = async () => {
    const v = confirming;
    if (!v) return;
    setBusy(true);
    try {
      const rec = await canvasApi.restoreVersion(canvasId, v.id);
      toast.success(`Restored version ${v.version_number}`);
      setConfirming(null);
      await onRestored?.(rec);
      load();
    } catch { toast.error('Restore failed'); }
    finally { setBusy(false); }
  };

  return createPortal(
    // The confirm sits OUTSIDE the backdrop: a portal still bubbles events
    // through the React tree, so a click inside it would reach this backdrop's
    // onMouseDown and close the history dialog underneath.
    <>
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(10,12,20,0.5)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: '92vw', maxHeight: '82vh', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18, boxShadow: '0 30px 80px -20px rgba(0,0,0,0.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, font: `700 16px ${T.font}`, color: T.fg1 }}><History size={18} /> Version history</span>
          <button type="button" onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: T.fg5, display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}` }}>
          <button type="button" onClick={snapshot} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 15px', borderRadius: 9, border: 0, cursor: 'pointer', background: T.gradient, color: '#fff', font: `600 13px ${T.font}`, boxShadow: '0 4px 10px -3px rgba(245,75,1,0.5)' }}>
            <Camera size={15} /> {busy ? 'Working…' : 'Save version'}
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 12 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: T.fg7, font: `500 13px ${T.font}` }}>Loading…</div>
          ) : versions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: T.fg7, font: `400 13px ${T.font}` }}>
              No versions yet. Save one to create a restore point.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {versions.map((v) => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', border: `1px solid ${T.border}`, borderRadius: 11, background: 'var(--em-bg-card)' }}>
                  <span style={{ width: 34, height: 34, borderRadius: 9, background: T.orange50, color: T.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, font: `700 13px ${T.font}` }}>v{v.version_number}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: `600 13px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.label || `Version ${v.version_number}`}</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>
                      <Clock size={11} /> {fmtDate(v.created_at)} · {v.created_by_name || 'Unknown'}
                    </div>
                  </div>
                  <button type="button" onClick={() => setConfirming(v)} disabled={busy} data-tip="Restore this version"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.fg2, cursor: 'pointer', font: `600 12px ${T.font}` }}>
                    <RotateCcw size={13} /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

    <ConfirmDialog
      open={!!confirming}
      title={`Restore version ${confirming?.version_number ?? ''}?`}
      message="Your current board is saved as a new version first, so nothing is lost."
      confirmLabel="Restore"
      busy={busy}
      onConfirm={restore}
      onCancel={() => !busy && setConfirming(null)}
    />
    </>,
    document.body
  );
}
